import { useEffect } from 'react';
import PropTypes from 'prop-types';
import '../Components/Styles/ImageNotification.css';

/**
 * ImageNotification Component
 * Displays toast notifications for image upload/capture operations
 * 
 * @param {Object} props
 * @param {string} props.message - Notification message
 * @param {string} props.type - Notification type: 'success', 'error', 'warning'
 * @param {Function} props.onClose - Callback when notification closes
 * @param {number} props.duration - Auto-close duration in ms (default: 3000)
 * @returns {JSX.Element}
 */
const ImageNotification = ({ message, type = 'success', onClose, duration = 3000 }) => {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      default:
        return 'ℹ';
    }
  };

  return (
    <div className={`image-notification ${type}`}>
      <div className="notification-content">
        <span className="notification-icon">{getIcon()}</span>
        <span className="notification-message">{message}</span>
      </div>
      <button 
        className="notification-close" 
        onClick={onClose}
        aria-label="Close notification"
      >
        ✕
      </button>
    </div>
  );
};

ImageNotification.propTypes = {
  message: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  onClose: PropTypes.func.isRequired,
  duration: PropTypes.number,
};

export default ImageNotification;
